import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { Box, HStack } from "@chakra-ui/layout";
import { arrayMove } from "@dnd-kit/sortable";

import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useBusinessContext } from "../../context/ModelContext";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { Checkbox } from "@chakra-ui/checkbox";
import { LoadingIndicator } from "../../components/LoadingIndicator/LoadingIndicator";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { Button, VStack, Text } from "@chakra-ui/react";
import { PlusIcon } from "../../styles/icons";
import { useToast } from "../../hooks/useToast";
import { useAdminApiUpdateRequest } from "../../hooks/useApiUpdatedRequest";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import {
  ExtendedFieldsTable,
  SortColumn,
  SortDirection,
} from "../../components/ExtendedFields/ExtendedFieldsTable";
import { useSearchInput } from "../../hooks/useSearchInput";
import { BusinessMenu } from "../../components/Business/BusinessMenu";
import { ExtendedFieldDeletionWarningModal } from "../../components/ExtendedFields/ExtendedFieldDeletionWarningModal";

export const ExtendedFields = () => {
  const business = useBusinessContext();
  const navigate = useNavigate();
  const [showDeletedFields, setShowDeletedFields] = useState(false);
  const onChangeDeletedCheck = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setShowDeletedFields(e.currentTarget.checked);
    },
    []
  );
  const [sortColumn, setSortColumn] = useState<SortColumn | null>("order");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [isOrderingMode, setIsOrderingMode] = useState<boolean>(false);
  const [visualOrder, setVisualOrder] = useState<string[]>([]);
  const [localFields, setLocalFields] = useState<
    CherryHubAdminApi.ExtendedFieldDefinition[] | null
  >(null);
  const [warningModalState, setWarningModalState] = useState<{
    isOpen: boolean;
    field: CherryHubAdminApi.ExtendedFieldDefinition | null;
    registrationForms: CherryHubAdminApi.RegistrationFormConfigurationSummaryItem[];
  }>({
    isOpen: false,
    field: null,
    registrationForms: [],
  });

  const { showErrorToast, showSuccessToast } = useToast();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();
  const apiClient = useAdminApiClient();

  const {
    debouncedQuery,
    setQuery,
    element: searchInputElement,
  } = useSearchInput({
    placeholder: "Search extended fields...",
    isDisabled: isOrderingMode,
  });

  const {
    data: fields,
    refresh,
    isLoading,
    error,
  } = useAdminApiRequest(
    (apiClient) =>
      apiClient.getBusinessExtendedFields(
        business.BusinessId!,
        showDeletedFields
      ),
    [business.BusinessId]
  );

  useEffect(() => {
    if (fields) {
      setLocalFields(fields);
    }
  }, [fields]);

  useEffect(() => {
    if (localFields && isOrderingMode) {
      const sorted = [...localFields].sort(
        (a, b) => (a.FieldOrder ?? 0) - (b.FieldOrder ?? 0)
      );
      setVisualOrder(sorted.map((f) => f.id));
    } else {
      setVisualOrder([]);
    }
  }, [localFields, isOrderingMode]);

  useEffect(() => {
    refresh();
  }, [showDeletedFields, refresh]);

  const filteredFields = useMemo(() => {
    const sourceFields = localFields || fields;
    if (!sourceFields) return null;

    if (isOrderingMode && visualOrder.length > 0) {
      const orderedFields: CherryHubAdminApi.ExtendedFieldDefinition[] = [];
      visualOrder.forEach((id) => {
        const field = sourceFields.find((f) => f.id === id);
        if (field) {
          orderedFields.push(field);
        }
      });
      return orderedFields;
    }

    let filtered = sourceFields.filter((field) => {
      if (!showDeletedFields && field.Meta?.IsDeleted) {
        return false;
      }

      if (debouncedQuery) {
        const query = debouncedQuery.toLowerCase();
        return (
          field.FieldName?.toLowerCase().includes(query) ||
          field.Description?.toLowerCase().includes(query) ||
          field.Owner?.toLowerCase().includes(query)
        );
      }

      return true;
    });

    if (sortColumn !== null) {
      filtered.sort((a, b) => {
        let result = 0;

        switch (sortColumn) {
          case "order":
            const orderA = a.FieldOrder ?? 0;
            const orderB = b.FieldOrder ?? 0;
            result = orderA - orderB;
            break;

          case "fieldName":
            const nameA = a.FieldName?.toLowerCase() ?? "";
            const nameB = b.FieldName?.toLowerCase() ?? "";
            result = nameA.localeCompare(nameB);
            break;

          case "owner":
            const ownerA = a.Owner?.toLowerCase() ?? "";
            const ownerB = b.Owner?.toLowerCase() ?? "";
            result = ownerA.localeCompare(ownerB);
            break;

          case "fieldType":
            const typeA = a.FieldType?.toLowerCase() ?? "";
            const typeB = b.FieldType?.toLowerCase() ?? "";
            result = typeA.localeCompare(typeB);
            break;

          case "version":
            const versionA = (a as any).Version ?? 0;
            const versionB = (b as any).Version ?? 0;
            result = versionA - versionB;
            break;

          case "created":
            const createdA = a.Meta?.DateCreated ?? "";
            const createdB = b.Meta?.DateCreated ?? "";
            result = createdA.localeCompare(createdB);
            break;

          case "lastModified":
            const updatedA = a.Meta?.DateUpdated ?? "";
            const updatedB = b.Meta?.DateUpdated ?? "";
            result = updatedA.localeCompare(updatedB);
            break;

          default:
            result = 0;
        }

        return sortDirection === "asc" ? result : -result;
      });
    }

    return filtered;
  }, [
    localFields,
    fields,
    debouncedQuery,
    showDeletedFields,
    sortColumn,
    sortDirection,
    isOrderingMode,
    visualOrder,
  ]);

  const deleteFieldRequest = useAdminApiUpdateRequest(
    (apiClient, fieldId: string) =>
      apiClient.deleteBusinessExtendedField(business.BusinessId!, fieldId),
    [fields]
  );

  const restoreFieldRequest = useAdminApiUpdateRequest(
    (apiClient, fieldId: string) =>
      apiClient.restoreBusinessExtendedField(business.BusinessId!, fieldId),
    [fields]
  );

  const onClickNewField = () =>
    navigate(`/admin/businesses/${business.BusinessId}/extended-fields/new`);

  const onClickEditField = (field: CherryHubAdminApi.ExtendedFieldDefinition) =>
    navigate(
      `/admin/businesses/${business.BusinessId}/extended-fields/${field.id}`
    );

  useEffect(() => {
    if (deleteFieldRequest.result) {
      if (deleteFieldRequest.result.ok) {
        showSuccessToast("Extended field deleted.");
        refresh();
      } else {
        showErrorToast(
          deleteFieldRequest.result.message ??
            "Failed to delete extended field."
        );
      }
    }
  }, [deleteFieldRequest.result, refresh]);

  const onClickDeleteField = useCallback(
    async (field: CherryHubAdminApi.ExtendedFieldDefinition) => {
      // First, check if any registration forms use this extended field
      const registrationFormsResult =
        await apiClient.getRegistrationFormsForExtendedField(
          business.BusinessId!,
          field.id
        );

      if (!registrationFormsResult.ok) {
        // Error fetching registration forms - show error and prevent deletion
        showErrorToast(
          registrationFormsResult.message ??
            "Failed to check for registration form dependencies. Cannot proceed with deletion."
        );
        return;
      }

      const registrationForms = registrationFormsResult.data || [];

      if (registrationForms.length === 0) {
        // No registration forms use this field - proceed with standard confirmation
        showConfirmationDialog(
          "Delete Extended Field?",
          `Are you sure you wish to delete extended field '${field.FieldName}'?`,
          "Delete"
        ).then(({ ok }) => {
          if (ok) {
            deleteFieldRequest.request(field.id);
          }
        });
      } else {
        // Registration forms use this field - show warning modal
        setWarningModalState({
          isOpen: true,
          field,
          registrationForms,
        });
      }
    },
    [
      apiClient,
      business.BusinessId,
      showConfirmationDialog,
      showErrorToast,
      deleteFieldRequest,
    ]
  );

  const handleWarningModalCancel = useCallback(() => {
    setWarningModalState({
      isOpen: false,
      field: null,
      registrationForms: [],
    });
  }, []);

  const handleWarningModalConfirm = useCallback(() => {
    if (warningModalState.field) {
      deleteFieldRequest.request(warningModalState.field.id);
      setWarningModalState({
        isOpen: false,
        field: null,
        registrationForms: [],
      });
    }
  }, [warningModalState.field, deleteFieldRequest]);

  useEffect(() => {
    if (restoreFieldRequest.result) {
      if (restoreFieldRequest.result.ok) {
        showSuccessToast("Extended field restored.");
        refresh();
      } else {
        showErrorToast(
          restoreFieldRequest.result.message ??
            "Failed to restore extended field."
        );
      }
    }
  }, [restoreFieldRequest.result, refresh]);

  const onClickRestoreField = useCallback(
    (field: CherryHubAdminApi.ExtendedFieldDefinition) =>
      showConfirmationDialog(
        "Restore Extended Field?",
        `Are you sure you wish to restore extended field '${field.FieldName}'?`,
        "Restore"
      ).then(({ ok }) => {
        if (ok) {
          restoreFieldRequest.request(field.id);
        }
      }),
    [showConfirmationDialog, restoreFieldRequest]
  );

  const handleSortChange = useCallback(
    (column: SortColumn) => {
      if (isOrderingMode) return;

      if (sortColumn === column) {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      } else {
        setSortColumn(column);
        setSortDirection("asc");
      }
    },
    [sortColumn, sortDirection, isOrderingMode]
  );

  const saveFieldOrderRequest = useAdminApiUpdateRequest(
    (apiClient, orderedFieldIds: string[]) =>
      apiClient.updateBusinessExtendedFieldOrder(
        business.BusinessId!,
        orderedFieldIds
      ),
    [fields]
  );

  useEffect(() => {
    if (saveFieldOrderRequest.result) {
      if (saveFieldOrderRequest.result.ok) {
        showSuccessToast("Field order updated successfully.");
        // Only trigger refresh after successful save
        setShowDeletedFields(false);
        setSortColumn("order");
        setSortDirection("asc");
        refresh();
      } else {
        showErrorToast(
          saveFieldOrderRequest.result.message ??
            "Failed to update field order."
        );
        // Refresh on error to restore correct state
        setShowDeletedFields(false);
        setSortColumn("order");
        setSortDirection("asc");
        refresh();
      }
    }
  }, [saveFieldOrderRequest.result, showErrorToast, showSuccessToast, refresh]);

  const handleReorderField = useCallback(
    (fieldId: string, newIndex: number) => {
      const oldIndex = visualOrder.findIndex((id) => id === fieldId);
      if (oldIndex === -1) return;

      const newVisualOrder = arrayMove(visualOrder, oldIndex, newIndex);
      setVisualOrder(newVisualOrder);
    },
    [visualOrder]
  );

  const handleEnterOrderingMode = useCallback(() => {
    setQuery("");
    setShowDeletedFields(false);
    setSortColumn("order");
    setSortDirection("asc");
    setIsOrderingMode(true);
  }, [setQuery]);

  const handleSaveFieldOrder = useCallback(() => {
    if (visualOrder.length > 0) {
      saveFieldOrderRequest.request(visualOrder);
    }
    // Exit ordering mode immediately to prevent further edits while saving
    setIsOrderingMode(false);
    // Note: showDeletedFields, sortColumn, and sortDirection are reset in the useEffect
    // after the API call completes to avoid race condition with refresh
  }, [visualOrder, saveFieldOrderRequest]);

  const handleCancelFieldOrder = useCallback(() => {
    setIsOrderingMode(false);
    setShowDeletedFields(false);
    setSortColumn("order");
    setSortDirection("asc");
  }, []);

  return (
    <>
      <PageHeading>
        <PageHeading.Title>Extended Fields</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack w="100%" alignItems="flex-start">
          <BusinessBreadcrumbs variant="pageHeading" mb={4}>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${business.BusinessId}/extended-fields`}
              >
                Extended Fields
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>
          <HStack w="100%" spacing={4}>
            <Box flex="4" maxWidth="400px">
              {searchInputElement}
            </Box>
            <Box>
              <Checkbox
                isChecked={showDeletedFields}
                onChange={onChangeDeletedCheck}
                title="Include deleted extended fields in the list."
                isDisabled={isOrderingMode}
              >
                <Text fontSize="sm">Include Deleted</Text>
              </Checkbox>
            </Box>
            <Box flex="1" />
            {isOrderingMode && (
              <Box>
                <Button size="sm" onClick={handleCancelFieldOrder}>
                  Cancel
                </Button>
              </Box>
            )}
            {isOrderingMode && (
              <Box>
                <Button
                  size="sm"
                  colorScheme="green"
                  onClick={handleSaveFieldOrder}
                  isLoading={saveFieldOrderRequest.isLoading}
                >
                  Save
                </Button>
              </Box>
            )}
            {!isOrderingMode && (
              <Box>
                <Button size="sm" onClick={handleEnterOrderingMode}>
                  Edit Order
                </Button>
              </Box>
            )}
            {!isOrderingMode && (
              <Box>
                <BusinessMenu
                  businessId={business.Meta?.Id!}
                  leftIcon={<PlusIcon />}
                  onClick={onClickNewField}
                >
                  New Extended Field
                </BusinessMenu>
              </Box>
            )}
          </HStack>

          <ExtendedFieldsTable
            fields={filteredFields}
            isLoading={isLoading}
            disableActions={
              deleteFieldRequest.isLoading || saveFieldOrderRequest.isLoading
            }
            debouncedQuery={debouncedQuery}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            isOrderingMode={isOrderingMode}
            onClickEditField={onClickEditField}
            onClickDeleteField={onClickDeleteField}
            onClickRestoreField={onClickRestoreField}
            onSortChange={handleSortChange}
            onReorderField={handleReorderField}
          />
          {error != null && (
            <ErrorMessage>
              {error ??
                "An error was encountered while loading extended fields."}
            </ErrorMessage>
          )}
          {(!fields || isLoading) && <LoadingIndicator />}
          {confirmationElement}
          <ExtendedFieldDeletionWarningModal
            isOpen={warningModalState.isOpen}
            fieldName={warningModalState.field?.FieldName || ""}
            businessId={business.BusinessId!}
            registrationForms={warningModalState.registrationForms}
            onCancel={handleWarningModalCancel}
            onConfirm={handleWarningModalConfirm}
          />
        </VStack>
      </PageContent>
    </>
  );
};
